import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { roleService } from './roleService';
import { Role, Permission, UserRoleAssignment, AuditLog, RoleVersion, ScopeType } from './types';
import { EmailGovernanceWorkspace } from '../administration/EmailGovernanceWorkspace';
import { TablePagination, useTablePagination } from '../../shared/TablePagination';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const RolesPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'roles' | 'email_governance' | 'permissions' | 'assignments' | 'evaluator' | 'audit'>('roles');
  const [selectedRoleForEmail, setSelectedRoleForEmail] = useState<string>('SAFETY_MANAGER');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Data states
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);

  // Create role form state
  const [newCode, setNewCode] = useState('');
  const [newName, setNewName] = useState('');
  const [newDesc, setNewDesc] = useState('');

  // Assign role form state
  const [assignUserId, setAssignUserId] = useState('USR-SAFETY-DIRECTOR');
  const [assignRoleId, setAssignRoleId] = useState('');
  const [assignScopeType, setAssignScopeType] = useState<ScopeType>('ORGANIZATION');
  const [assignScopeId, setAssignScopeId] = useState('');
  const [userAssignments, setUserAssignments] = useState<UserRoleAssignment[]>([]);

  // Access evaluator form state
  const [evalUserId, setEvalUserId] = useState('USR-SAFETY-DIRECTOR');
  const [evalPermission, setEvalPermission] = useState('report:create');
  const [evalScopeType, setEvalScopeType] = useState<ScopeType>('ORGANIZATION');
  const [evalScopeId, setEvalScopeId] = useState('');
  const [evalResult, setEvalResult] = useState<{ allowed: boolean; reason?: string; matchedRole?: string; matchedScope?: string } | null>(null);

  // Version modal state
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [selectedRoleForVersions, setSelectedRoleForVersions] = useState<Role | null>(null);

  const { sortedItems: sortedRoles, sortField: roleSortField, sortDirection: roleSortDir, toggleSort: toggleRoleSort } = useTableSort(roles);
  const { sortedItems: sortedPermissions, sortField: permSortField, sortDirection: permSortDir, toggleSort: togglePermSort } = useTableSort(permissions);
  const { sortedItems: sortedAuditLogs, sortField: auditSortField, sortDirection: auditSortDir, toggleSort: toggleAuditSort } = useTableSort(auditLogs);
  const { sortedItems: sortedAssignments, sortField: asgSortField, sortDirection: asgSortDir, toggleSort: toggleAsgSort } = useTableSort(userAssignments);

  const {
    currentPage: permPage,
    setCurrentPage: setPermPage,
    pageSize: permPageSize,
    setPageSize: setPermPageSize,
    totalItems: totalPermItems,
    paginatedItems: paginatedPermissions,
  } = useTablePagination(sortedPermissions, 25);

  const {
    currentPage: auditPage,
    setCurrentPage: setAuditPage,
    pageSize: auditPageSize,
    setPageSize: setAuditPageSize,
    totalItems: totalAuditItems,
    paginatedItems: paginatedAuditLogs,
  } = useTablePagination(sortedAuditLogs, 25);
  const [roleVersions, setRoleVersions] = useState<RoleVersion[]>([]);
  const [snapshotReason, setSnapshotReason] = useState('');

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (activeTab === 'assignments' && assignUserId) {
      loadUserAssignments(assignUserId);
    }
  }, [activeTab, assignUserId]);

  const loadInitialData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [rolesData, permsData, logsData] = await Promise.all([
        roleService.listRoles(),
        roleService.listPermissions(),
        roleService.getAuditLogs(),
      ]);
      setRoles(rolesData);
      setPermissions(permsData);
      setAuditLogs(logsData);
      if (rolesData.length > 0 && !assignRoleId) {
        setAssignRoleId(rolesData[0].id);
      }
      if (permsData.length > 0 && !evalPermission) {
        setEvalPermission(permsData[0].code);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadUserAssignments = async (userId: string) => {
    try {
      const assignments = await roleService.getUserAssignments(userId);
      setUserAssignments(assignments);
    } catch (err: any) {
      console.error('Failed to load user assignments', err);
    }
  };

  const handleCreateRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      await roleService.createRole({
        code: newCode,
        name: newName,
        description: newDesc,
        roleType: 'CUSTOM',
      });
      setSuccessMsg(`Role ${newName} successfully created.`);
      setNewCode('');
      setNewName('');
      setNewDesc('');
      await loadInitialData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDeleteRole = async (roleId: string) => {
    if (!confirm('Are you sure you want to deactivate this role?')) return;
    try {
      setError(null);
      await roleService.deleteRole(roleId);
      setSuccessMsg('Role deactivated successfully.');
      await loadInitialData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAssignRole = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      await roleService.assignRole({
        userId: assignUserId,
        roleId: assignRoleId,
        scopeType: assignScopeType,
        scopeId: assignScopeId || undefined,
      });
      setSuccessMsg(`Role assigned to user ${assignUserId}.`);
      await loadUserAssignments(assignUserId);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleViewVersions = async (role: Role) => {
    setSelectedRoleForVersions(role);
    setShowVersionModal(true);
    try {
      const versions = await roleService.getRoleVersions(role.id);
      setRoleVersions(versions);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleCreateSnapshot = async () => {
    if (!selectedRoleForVersions) return;
    try {
      setError(null);
      await roleService.createRoleSnapshot(selectedRoleForVersions.id, snapshotReason || undefined);
      setSuccessMsg('New immutable role snapshot created.');
      setSnapshotReason('');
      const versions = await roleService.getRoleVersions(selectedRoleForVersions.id);
      setRoleVersions(versions);
      await loadInitialData();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleEvaluate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      const res = await roleService.evaluateAccess({
        userId: evalUserId,
        requiredPermission: evalPermission,
        targetScopeType: evalScopeType,
        targetScopeId: evalScopeId || undefined,
      });
      setEvalResult(res);
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div>
      {/* 1. FlowDesk Hero Header Banner */}
      <div className="page-header-banner">
        <div className="badge">SECURITY & ACCESS GOVERNANCE</div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h1>Role-Based Access Control (RBAC)</h1>
            <p>
              Enterprise role templates, granular permission definitions, scoped user assignments, and authorization evaluation simulator.
            </p>
          </div>
          <div>
            <span className="badge-pill badge-info" style={{ fontSize: '12px', padding: '6px 14px' }}>
              Central Authorization Engine
            </span>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
          <strong>Error:</strong> {error}
        </div>
      )}
      {successMsg && (
        <div style={{ background: '#dcfce7', color: '#166534', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', fontSize: '14px' }}>
          <strong>Success:</strong> {successMsg}
        </div>
      )}

      {/* 2. Navigation Tabs */}
      <div className="nav-tabs">
        <button
          onClick={() => {
            setActiveTab('roles');
            setError(null);
            setSuccessMsg(null);
          }}
          className={`tab-btn ${activeTab === 'roles' ? 'active' : ''}`}
        >
          🔑 Roles ({roles.length})
        </button>
        <button
          onClick={() => {
            setActiveTab('email_governance');
            setError(null);
            setSuccessMsg(null);
          }}
          className={`tab-btn ${activeTab === 'email_governance' ? 'active' : ''}`}
        >
          📧 Email Configuration
        </button>
        <button
          onClick={() => {
            setActiveTab('permissions');
            setError(null);
            setSuccessMsg(null);
          }}
          className={`tab-btn ${activeTab === 'permissions' ? 'active' : ''}`}
        >
          🛡️ Permissions ({permissions.length})
        </button>
        <button
          onClick={() => {
            setActiveTab('assignments');
            setError(null);
            setSuccessMsg(null);
          }}
          className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
        >
          👤 User Role Assignments
        </button>
        <button
          onClick={() => {
            setActiveTab('evaluator');
            setError(null);
            setSuccessMsg(null);
          }}
          className={`tab-btn ${activeTab === 'evaluator' ? 'active' : ''}`}
        >
          🧪 Access Decision Simulator
        </button>
        <button
          onClick={() => {
            setActiveTab('audit');
            setError(null);
            setSuccessMsg(null);
          }}
          className={`tab-btn ${activeTab === 'audit' ? 'active' : ''}`}
        >
          📋 Security Audit Trail
        </button>
      </div>

      {/* 1. ROLES TAB */}
      {activeTab === 'roles' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '24px' }}>
          {/* Roles Table */}
          <div className="table-container" style={{ marginBottom: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <SortableTh field="code" currentField={roleSortField} currentDirection={roleSortDir} onSort={toggleRoleSort}>
                    Role Code
                  </SortableTh>
                  <SortableTh field="name" currentField={roleSortField} currentDirection={roleSortDir} onSort={toggleRoleSort}>
                    Role Name
                  </SortableTh>
                  <SortableTh field="roleType" currentField={roleSortField} currentDirection={roleSortDir} onSort={toggleRoleSort}>
                    Type
                  </SortableTh>
                  <SortableTh field="versionNumber" currentField={roleSortField} currentDirection={roleSortDir} onSort={toggleRoleSort}>
                    Version
                  </SortableTh>
                  <SortableTh field="isActive" currentField={roleSortField} currentDirection={roleSortDir} onSort={toggleRoleSort}>
                    Status
                  </SortableTh>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedRoles.map((r) => (
                  <tr key={r.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{r.code}</td>
                    <td style={{ fontWeight: 600 }}>{r.name}</td>
                    <td>
                      <span className={`badge-pill ${r.isSystem ? 'badge-info' : 'badge-warning'}`}>
                        {r.roleType}
                      </span>
                    </td>
                    <td>
                      <span className="badge-pill badge-neutral">v{r.versionNumber || 1}</span>
                    </td>
                    <td>
                      <span className={`badge-pill ${r.isActive ? 'badge-success' : 'badge-danger'}`}>
                        {r.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '6px' }}>
                        <button
                          onClick={() => {
                            setSelectedRoleForEmail(r.code);
                            setActiveTab('email_governance');
                          }}
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '12px', color: '#0284c7', borderColor: '#bae6fd' }}
                        >
                          📧 Email Config
                        </button>
                        <button
                          onClick={() => handleViewVersions(r)}
                          className="btn-secondary"
                          style={{ padding: '4px 10px', fontSize: '12px' }}
                        >
                          Versions
                        </button>
                        {!r.isSystem && (
                          <button
                            onClick={() => handleDeleteRole(r.id)}
                            className="btn-secondary"
                            style={{ padding: '4px 10px', fontSize: '12px', color: '#dc2626', borderColor: '#fca5a5' }}
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Create Role Card */}
          <div className="card" style={{ marginBottom: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '16px' }}>
              Add Custom Enterprise Role
            </h3>
            <form onSubmit={handleCreateRole} className="login-form">
              <div className="form-field">
                <label>Role Code (e.g. LINE_AUDITOR)</label>
                <input
                  type="text"
                  value={newCode}
                  onChange={(e) => setNewCode(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Role Display Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  value={newDesc}
                  onChange={(e) => setNewDesc(e.target.value)}
                  rows={3}
                  style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px', fontFamily: 'inherit' }}
                />
              </div>
              <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>
                Save Custom Role
              </button>
            </form>
          </div>
        </div>
      )}

      {/* 1B. ROLE EMAIL & NOTIFICATION GOVERNANCE TAB (A-Z) */}
      {activeTab === 'email_governance' && (
        <EmailGovernanceWorkspace
          defaultSubTab="role_matrix"
          initialSelectedRoleCode={selectedRoleForEmail}
          externalRoles={roles.map((r) => ({ code: r.code, name: r.name }))}
        />
      )}

      {/* 2. PERMISSIONS TAB */}
      {activeTab === 'permissions' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="code" currentField={permSortField} currentDirection={permSortDir} onSort={togglePermSort}>
                  Permission Code
                </SortableTh>
                <SortableTh field="module" currentField={permSortField} currentDirection={permSortDir} onSort={togglePermSort}>
                  Domain Module
                </SortableTh>
                <SortableTh field="action" currentField={permSortField} currentDirection={permSortDir} onSort={togglePermSort}>
                  Action
                </SortableTh>
                <SortableTh field="description" currentField={permSortField} currentDirection={permSortDir} onSort={togglePermSort}>
                  Description
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {paginatedPermissions.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e293b' }}>{p.code}</td>
                  <td>
                    <span className="badge-pill badge-neutral">{p.module}</span>
                  </td>
                  <td>{p.action}</td>
                  <td style={{ color: '#64748b' }}>{p.description || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            currentPage={permPage}
            totalItems={totalPermItems}
            pageSize={permPageSize}
            onPageChange={setPermPage}
            onPageSizeChange={setPermPageSize}
            itemLabel="permissions"
          />
        </div>
      )}

      {/* 3. ASSIGNMENTS TAB */}
      {activeTab === 'assignments' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '24px' }}>
          <div className="card" style={{ marginBottom: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '16px' }}>
              Assign Role & Scope to User
            </h3>
            <form onSubmit={handleAssignRole} className="login-form">
              <div className="form-field">
                <label>User Identifier / Username</label>
                <input
                  type="text"
                  value={assignUserId}
                  onChange={(e) => setAssignUserId(e.target.value)}
                  required
                />
              </div>
              <div className="form-field">
                <label>Assigned Role</label>
                <select
                  value={assignRoleId}
                  onChange={(e) => setAssignRoleId(e.target.value)}
                >
                  {roles.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name} ({r.code})
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field">
                <label>Authorization Scope</label>
                <select
                  value={assignScopeType}
                  onChange={(e) => setAssignScopeType(e.target.value as ScopeType)}
                >
                  <option value="GLOBAL">GLOBAL (Entire Enterprise)</option>
                  <option value="ORGANIZATION">ORGANIZATION</option>
                  <option value="DEPARTMENT">DEPARTMENT</option>
                  <option value="LOCATION">LOCATION</option>
                </select>
              </div>
              <div className="form-field">
                <label>Scope ID (Department / Base Location)</label>
                <input
                  type="text"
                  placeholder="e.g. dept-ist-flight-ops"
                  value={assignScopeId}
                  onChange={(e) => setAssignScopeId(e.target.value)}
                />
              </div>
              <button type="submit" className="btn-primary" style={{ marginTop: '8px' }}>
                Assign Role to User
              </button>
            </form>
          </div>

          <div className="card" style={{ marginBottom: 0 }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: '#1e293b', marginBottom: '16px' }}>
              Active User Role Grants ({assignUserId})
            </h3>
            <div className="table-container" style={{ marginBottom: 0 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <SortableTh field="roleId" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort}>
                      Role
                    </SortableTh>
                    <SortableTh field="scopeType" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort}>
                      Scope Type
                    </SortableTh>
                    <SortableTh field="scopeId" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort}>
                      Scope ID
                    </SortableTh>
                    <SortableTh field="isActive" currentField={asgSortField} currentDirection={asgSortDir} onSort={toggleAsgSort}>
                      Status
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {sortedAssignments.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>
                        No roles assigned to this user ID yet.
                      </td>
                    </tr>
                  ) : (
                    sortedAssignments.map((a) => (
                      <tr key={a.id}>
                        <td style={{ fontWeight: 600 }}>{a.role?.name || a.roleId}</td>
                        <td>{a.scopeType}</td>
                        <td style={{ fontFamily: 'monospace', color: '#64748b' }}>{a.scopeId || 'ALL'}</td>
                        <td>
                          <span className={`badge-pill ${a.isActive ? 'badge-success' : 'badge-danger'}`}>
                            {a.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* 4. EVALUATOR TAB */}
      {activeTab === 'evaluator' && (
        <div className="card" style={{ maxWidth: '720px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 700, color: '#1e293b', marginBottom: '6px' }}>
            Authorization Decision Simulator
          </h2>
          <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
            Simulate backend <code>evaluateAccess</code> policy decisions in real time with scoped RBAC checks.
          </p>

          <form onSubmit={handleEvaluate} className="login-form">
            <div className="form-field">
              <label>User Identifier</label>
              <input
                type="text"
                value={evalUserId}
                onChange={(e) => setEvalUserId(e.target.value)}
                required
              />
            </div>
            <div className="form-field">
              <label>Requested Permission</label>
              <select
                value={evalPermission}
                onChange={(e) => setEvalPermission(e.target.value)}
              >
                {permissions.map((p) => (
                  <option key={p.id} value={p.code}>
                    {p.code} ({p.module})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label>Target Scope Type</label>
              <select
                value={evalScopeType}
                onChange={(e) => setEvalScopeType(e.target.value as ScopeType)}
              >
                <option value="ORGANIZATION">ORGANIZATION</option>
                <option value="DEPARTMENT">DEPARTMENT</option>
                <option value="LOCATION">LOCATION</option>
                <option value="GLOBAL">GLOBAL</option>
              </select>
            </div>
            <div className="form-field">
              <label>Target Scope ID (Optional)</label>
              <input
                type="text"
                placeholder="e.g. dept-ist-flight-ops"
                value={evalScopeId}
                onChange={(e) => setEvalScopeId(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary" style={{ background: '#0f172a', marginTop: '8px' }}>
              Evaluate Access Policy
            </button>
          </form>

          {evalResult && (
            <div
              style={{
                marginTop: '20px',
                padding: '16px',
                borderRadius: '8px',
                background: evalResult.allowed ? '#dcfce7' : '#fee2e2',
                border: `1px solid ${evalResult.allowed ? '#86efac' : '#fca5a5'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '20px' }}>{evalResult.allowed ? '✔' : '✖'}</span>
                <strong style={{ fontSize: '16px', color: evalResult.allowed ? '#166534' : '#991b1b' }}>
                  {evalResult.allowed ? 'ACCESS GRANTED (ALLOWED)' : 'ACCESS DENIED'}
                </strong>
              </div>
              <p style={{ margin: '8px 0 0 0', fontSize: '13px', color: evalResult.allowed ? '#14532d' : '#7f1d1d' }}>
                {evalResult.reason || `Matched Role: ${evalResult.matchedRole} (Scope: ${evalResult.matchedScope})`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* 5. AUDIT TAB */}
      {activeTab === 'audit' && (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh field="createdAt" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort}>
                  Timestamp
                </SortableTh>
                <SortableTh field="entityType" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort}>
                  Entity Type
                </SortableTh>
                <SortableTh field="action" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort}>
                  Action
                </SortableTh>
                <SortableTh field="entityId" currentField={auditSortField} currentDirection={auditSortDir} onSort={toggleAuditSort}>
                  Entity Identifier
                </SortableTh>
              </tr>
            </thead>
            <tbody>
              {paginatedAuditLogs.map((log) => (
                <tr key={log.id}>
                  <td style={{ color: '#64748b' }}>{new Date(log.createdAt).toLocaleString()}</td>
                  <td style={{ fontWeight: 600 }}>{log.entityType}</td>
                  <td>
                    <span
                      className={`badge-pill ${
                        log.action === 'CREATE' ? 'badge-success' : log.action === 'DELETE' ? 'badge-danger' : 'badge-neutral'
                      }`}
                    >
                      {log.action}
                    </span>
                  </td>
                  <td style={{ fontFamily: 'monospace' }}>{log.entityId}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            currentPage={auditPage}
            totalItems={totalAuditItems}
            pageSize={auditPageSize}
            onPageChange={setAuditPage}
            onPageSizeChange={setAuditPageSize}
            itemLabel="audit logs"
          />
        </div>
      )}

      {/* Role Version History Modal */}
      {showVersionModal && selectedRoleForVersions && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div className="modal-header">
              <div className="modal-header-content">
                <h3>Role Version History (Immutable Snapshots)</h3>
                <p>
                  {selectedRoleForVersions.name} ({selectedRoleForVersions.code}) — {selectedRoleForVersions.roleType}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowVersionModal(false)}
                className="modal-close-btn"
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              {/* Create Snapshot Section */}
              <div style={{ background: '#f8fafc', padding: '14px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '16px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '13px', color: '#334155' }}>Create Version Snapshot</h4>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    placeholder="Revision justification (e.g. QMS Audit update)"
                    value={snapshotReason}
                    onChange={(e) => setSnapshotReason(e.target.value)}
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  />
                  <button
                    onClick={handleCreateSnapshot}
                    className="btn-primary"
                    style={{ width: 'auto', padding: '8px 16px' }}
                  >
                    Lock Snapshot (v{(selectedRoleForVersions.versionNumber || 1) + 1})
                  </button>
                </div>
              </div>

              {/* Versions Table */}
              <div className="table-container" style={{ marginBottom: 0 }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Version</th>
                      <th>Effective From</th>
                      <th>Effective Until</th>
                      <th>Permissions Count</th>
                      <th>Permissions Snapshot</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roleVersions.map((v) => (
                      <tr key={v.id}>
                        <td style={{ fontWeight: 600, color: '#2563eb' }}>v{v.versionNumber}</td>
                        <td style={{ color: '#64748b' }}>{new Date(v.effectiveFrom).toLocaleDateString()}</td>
                        <td style={{ color: '#64748b' }}>
                          {v.effectiveTo ? new Date(v.effectiveTo).toLocaleDateString() : 'Current (Active)'}
                        </td>
                        <td>{v.permissionSnapshot.length} perms</td>
                        <td style={{ maxWidth: '240px', fontSize: '11px', fontFamily: 'monospace' }}>
                          {v.permissionSnapshot.slice(0, 3).join(', ')}
                          {v.permissionSnapshot.length > 3 ? ` +${v.permissionSnapshot.length - 3} more` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowVersionModal(false)} className="btn-secondary">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RolesPage;
